import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { SectionCard } from '../../components/common/SectionCard';
import CircularProgress from '@mui/material/CircularProgress';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useAuth } from '../../contexts/AuthContext';
import { useGetOrganization, useListModels } from '../../api/generated/generated';
import type { Organization, ModelConfig } from '../../api/generated';

export function OrgDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: org, isLoading } = useGetOrganization(user?.orgId ?? '', {
    query: {
      select: (response): Organization | undefined =>
        response.status === 200 ? response.data : undefined,
    },
  });
  const { data: models } = useListModels({
    query: {
      select: (response): ModelConfig[] => response.data,
    },
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!org) {
    return (
      <Typography sx={{
        color: "text.secondary"
      }}>Organization not found.</Typography>
    );
  }

  const selectedModel = models?.find((m) => m.id === org.selected_model);

  return (
    <Box>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <PeopleIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="h6">Members</Typography>
            </Box>
            <Typography variant="h3" color="primary">
              {org.member_count}
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/organization/members')}
              sx={{ mt: 1 }}
            >
              Manage Members
            </Button>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SmartToyIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="h6">AI Model</Typography>
            </Box>
            <Typography variant="h5">
              {selectedModel?.name ?? org.selected_model}
            </Typography>
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              {selectedModel?.description ?? ''}
            </Typography>
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <SectionCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <SettingsIcon sx={{ mr: 1 }} color="primary" />
              <Typography variant="h6">Settings</Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mb: 1
              }}>
              Manage organization name, model, and other settings.
            </Typography>
            <Button
              size="small"
              onClick={() => navigate('/organization/settings')}
            >
              Go to Settings
            </Button>
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  );
}
